import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getServerUsersLoginHistoryAPI } from '../services/serverService';

const ServerLoginHistoryPage = () => {
    const { serverId } = useParams();
    const navigate = useNavigate();
    
    // State
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Controls
    const [filter, setFilter] = useState('All'); // 'All', 'Admin', 'Employee'
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' (Newest) or 'asc' (Oldest)

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                setIsLoading(true);
                const response = await getServerUsersLoginHistoryAPI(serverId);
                setUsers(response.data.data || []);
            } catch (error) {
                console.error("Failed to fetch login history", error);
                alert("Failed to load user login history.");
            } finally {
                setIsLoading(false);
            }
        };

        if (serverId) fetchHistory();
    }, [serverId]);

    // Apply Filter AND Sort dynamically
    const processedUsers = [...users]
        .filter(user => filter === 'All' ? true : user.role === filter)
        .sort((a, b) => {
            // Always push users with NO login history to the very bottom
            if (!a.lastSeen) return 1;
            if (!b.lastSeen) return -1;
            
            const timeA = new Date(a.lastSeen).getTime();
            const timeB = new Date(b.lastSeen).getTime();
            
            return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
        });

    const formatDate = (dateString) => {
        if (!dateString) return <span style={{ color: '#a0aec0' }}>Never Logged In</span>;
        return new Date(dateString).toLocaleString('en-GB', { 
            day: 'numeric', month: 'short', year: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
        });
    };

    return (
        <div className="page-container">
            <div className="page-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', marginBottom: '20px' }}>
                <Link to={`/servers/${serverId}`} style={{ textDecoration: 'none', color: '#718096', fontSize: '13px', fontWeight: '500' }}>
                    ← Back to Server Dashboard
                </Link>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <div>
                        <h1 style={{ margin: 0, color: '#2c3e50' }}>Users Login History</h1>
                        <p style={{ margin: '5px 0 0 0', color: '#718096' }}>Monitor access timestamps for all staff and admins.</p>
                    </div>
                    
                    {/* Controls Container */}
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        
                        {/* Role Filter */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {['All', 'Admin', 'Employee'].map(f => (
                                <button 
                                    key={f}
                                    onClick={() => setFilter(f)}
                                    style={{
                                        padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                                        fontWeight: 'bold', fontSize: '14px', transition: 'all 0.2s',
                                        backgroundColor: filter === f ? '#3182ce' : '#edf2f7',
                                        color: filter === f ? 'white' : '#4a5568',
                                        boxShadow: filter === f ? '0 2px 4px rgba(49, 130, 206, 0.3)' : 'none'
                                    }}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>

                        {/* Visual Divider */}
                        <div style={{ width: '1px', height: '24px', backgroundColor: '#cbd5e0' }}></div>

                        {/* Sort Toggle */}
                        <button 
                            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                            style={{
                                padding: '8px 16px', borderRadius: '20px', border: '1px solid #cbd5e0', cursor: 'pointer',
                                fontWeight: 'bold', fontSize: '14px', backgroundColor: 'white', color: '#4a5568',
                                display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                            {sortOrder === 'desc' ? '↓ Newest First' : '↑ Oldest First'}
                        </button>

                    </div>
                </div>
            </div>

            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>User Name</th>
                            <th>Email Address</th>
                            <th>Role</th>
                            <th>Last Access</th>
                            <th>Device Access Info</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="5" className="no-data-cell">Loading history...</td></tr>
                        ) : processedUsers.length > 0 ? processedUsers.map((user) => (
                            <tr key={user.uid}>
                                <td style={{ fontWeight: 'bold', color: '#2d3748' }}>{user.fullName}</td>
                                <td style={{ color: '#4a5568' }}>{user.email}</td>
                                <td>
                                    <span style={{
                                        backgroundColor: user.role === 'Admin' ? '#ebf8ff' : '#f0fff4',
                                        color: user.role === 'Admin' ? '#3182ce' : '#38a169',
                                        padding: '4px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold'
                                    }}>
                                        {user.role}
                                    </span>
                                </td>
                                <td>{formatDate(user.lastSeen)}</td>
                                <td>
                                    <button 
                                        className="action-button view"
                                        style={{ padding: '6px 12px', backgroundColor: '#edf2f7', color: '#2b6cb0', fontWeight: 'bold', border: '1px solid #e2e8f0' }}
                                        onClick={() => navigate(`/servers/${serverId}/login-history/${user.role}/${user.id}`)}
                                        disabled={!user.lastSeen}
                                        title={!user.lastSeen ? "No device data available" : "View Devices"}
                                    >
                                        View Details →
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="5" className="no-data-cell">No users found for this criteria.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ServerLoginHistoryPage;